import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Spinner,
  useDisclosure,
  Tooltip,
  Collapse,
  useColorMode,
  Img,
} from '@chakra-ui/react';
import {
  Composition,
  compositionApi,
  CompositionDimensionGroup,
  DimensionValue,
} from '@texas/api/endpoints/compositionApi';
import {
  DimensionGroup,
  dimensionGroupsApi,
  Metric,
} from '@texas/api/endpoints/metadata/dimensionGroupsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { EmptyDataButton } from '@texas/components/shared/button/EmptyDataButton';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import {
  fadeInRightAnimation,
  moveDownRotateAnimation,
} from '@texas/resources/animations/animations';
import { navLinkOffsetStyle } from '@texas/resources/styles';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect, useState } from 'react';
import { ErrorDetails } from '../../alert/ErrorDetails';
import { TexasSelect } from '../../form/TexasSelect';
import {
  convertToEnum,
  getEnumNamesAndValues,
} from '@texas/utils/helpers/enumHelpers';
import { TexasFormLabel } from '../../form/TexasFormLabel';
import { onlyUnique } from '@texas/utils/helpers/commonHelpers';
import { metricFriendlyName, metricShortName } from './translations';
import { SimpleNote } from '../../SimpleNote';
import { useTranslation } from 'react-i18next';
import { TopRightContainerComponent } from '../../TopRightContainerComponent';
import { MetadataList, MetadataListRow } from '../../MetadataList';
import { clientEndpoints } from '@texas/clientEndpoints';

export type DimensionGroupMode =
  | 'only-group'
  | 'only-values'
  | 'read-only'
  | 'read-only-group';

export function DimensionGroupContainer({
  composition,
  mode,
  onGroupChange,
  isReady,
  isMatrix,
  children: dimensionForm,
}: {
  composition: Composition;
  mode?: DimensionGroupMode;
  onGroupChange: (dimension: CompositionDimensionGroup) => void;
  isReady: boolean;
  isMatrix: boolean;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  if (mode == 'read-only' || mode == 'read-only-group') {
    return (
      <>
        <TopRightContainerComponent bar={<Icons.Lock />}>
          <Flex
            sx={{
              ':hover *[data-hover-id="text"]': {
                height: '12px',
                opacity: 1,
              },
            }}
            bg="texas.bg.700"
            _light={{ bg: 'gray.50' }}
            borderRadius="md"
            padding={2}
            cursor="pointer"
            alignItems="center"
            justifyContent="space-between"
          >
            <Flex flexDir="column" justify="center" h="46px">
              <Flex gap={2}>
                <Heading fontSize="md">
                  {composition.dimensionGroup?.name ??
                    t('composition.noDimensions')}
                </Heading>
              </Flex>
              <Text
                data-hover-id="text"
                height="0"
                opacity={0}
                transition="height 200ms ease, opacity 200ms ease"
                fontSize="sm"
              >
                {t('sizes.noEdit')}
              </Text>
            </Flex>
            {composition.dimensionGroup?.visualFileIdentifier && (
              <Img
                maxH="24"
                objectFit="contain"
                borderRadius="md"
                w="fit-content"
                src={clientEndpoints.previewImage(
                  composition.dimensionGroup.visualFileIdentifier,
                  128,
                )}
              />
            )}
          </Flex>
        </TopRightContainerComponent>
        {!isMatrix && mode != 'read-only-group' && (
          <Box pt={2}>
            <Text
              fontFamily="Barlow"
              fontWeight="bold"
              textTransform="uppercase"
              pb={2}
            >
              {t('configuration.dimensions')}
            </Text>
            <MetadataList>
              {composition.dimensionGroup?.dimensions.map((d) => {
                return (
                  <MetadataListRow key={d.id} label={d.label}>
                    {d.value} {metricShortName(d.metric)}
                  </MetadataListRow>
                );
              })}
            </MetadataList>
          </Box>
        )}
      </>
    );
  }

  return (
    <Box>
      {mode !== 'only-values' && (
        <ChangeDimensionGroup
          composition={composition}
          onChange={onGroupChange}
          isReady={isReady}
        />
      )}

      {mode !== 'only-group' && <Box pt={2}>{dimensionForm}</Box>}
    </Box>
  );
}

function ChangeDimensionGroup({
  onChange,
  composition,
  isReady,
}: {
  onChange: (dimension: CompositionDimensionGroup) => void;
  composition: Composition;
  isReady: boolean;
}) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedIds, setSelectedIds] = useState<number[] | null>(
    composition.dimensionGroup?.dimensions.map((d) => d.id) ?? null,
  );
  const firstDimension = composition.dimensionGroup?.dimensions.at(0);
  const [metric, setMetric] = useState<Metric>(
    firstDimension
      ? convertToEnum(Metric, firstDimension.metric)
      : Metric.Millimeters,
  );

  const {
    request: setRequest,
    loading,
    error,
  } = useApiRequest(compositionApi.setDimensionGroup);

  const updateRequest = async () => {
    if (!selectedIds) return;

    await request(
      setRequest,
      [composition.id, { dimensionIds: selectedIds, metric: metric }],
      (data) => {
        onClose();
        onChange(data);
      },
    );
  };

  return (
    <>
      {composition.dimensionGroup ? (
        <Flex
          onClick={onOpen}
          sx={{
            ':hover *[data-hover-id="text"]': {
              height: '12px',
              opacity: 1,
            },
          }}
          bg="texas.bg.700"
          _light={{ bg: 'gray.50' }}
          borderRadius="md"
          padding={2}
          cursor="pointer"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex flexDir="column" justify="center" h="46px">
            <Flex gap={2}>
              <Heading fontSize="md">{composition.dimensionGroup.name}</Heading>
              <MetricTags dimensions={composition.dimensionGroup.dimensions} />
            </Flex>
            <Text
              height="0"
              opacity={0}
              transition="height 200ms ease, opacity 200ms ease"
              fontSize="sm"
              data-hover-id="text"
            >
              {t('composition.changeDimensions')}
            </Text>
          </Flex>
          {composition.dimensionGroup.visualFileIdentifier && (
            <Img
              objectFit="contain"
              borderRadius="md"
              h="16"
              src={clientEndpoints.previewImage(
                composition.dimensionGroup.visualFileIdentifier,
                64,
              )}
            />
          )}
        </Flex>
      ) : (
        <EmptyDataButton
          icon={<Icons.AxisArrow />}
          onClick={onOpen}
          label={t('sizes.noSelected')}
          description={t('sizes.viewAvailable')}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('sizes.selectDimensionGroup')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ErrorDetails error={error} />
            <InnerModal
              onChange={(ids) => setSelectedIds(ids)}
              onMetricChange={(m) => setMetric(m)}
              selectedMetric={metric}
              composition={composition}
            />
          </ModalBody>

          <ModalFooter>
            <Flex direction="column" align="flex-end">
              {isReady && (
                <SimpleNote
                  py={2}
                  color="yellow.400"
                  _light={{ color: 'orange.500' }}
                >
                  <Text>{t('sizes.selectAndRevertIsReady')}</Text>
                </SimpleNote>
              )}
              <Button
                w="fit-content"
                isDisabled={selectedIds === null || loading}
                variant="texas-solid"
                isLoading={loading}
                onClick={async () => {
                  await updateRequest();
                }}
              >
                {t('sizes.selectDimensions')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function MetricTags({ dimensions }: { dimensions: DimensionValue[] }) {
  const metrics = dimensions
    .map((d) => convertToEnum(Metric, d.metric))
    .filter(onlyUnique);

  return (
    <>
      {metrics.map((m) => (
        <Badge h="fit-content" key={m}>
          {metricFriendlyName(m)}
        </Badge>
      ))}
    </>
  );
}

function InnerModal({
  onChange,
  onMetricChange,
  composition,
  selectedMetric,
}: {
  onChange: (ids: number[]) => void;
  onMetricChange: (metric: Metric) => void;
  selectedMetric: Metric;
  composition: Composition;
}) {
  const { t } = useTranslation();
  const {
    data: groups,
    refetch: refetchGroups,
    loading: groupsLoading,
  } = useApiResource(dimensionGroupsApi.getAll);

  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(
    composition.dimensionGroup?.id,
  );

  useEffect(() => {
    refetchGroups();
  }, [refetchGroups]);

  const selectedDimensionGroup = groups?.find((x) => x.id === selectedGroupId);
  const firstDimension = composition.dimensionGroup?.dimensions.at(0);
  const { isOpen, onToggle } = useDisclosure();

  if (groupsLoading) return <Spinner />;

  const hasRecommendedGroups = groups?.some((g) =>
    g.linkedProductGroupIds.some(
      (l) => composition.articleProductGroupId === l,
    ),
  );

  return (
    <Flex gap={6}>
      <Flex flexDir="column" gap={2} minW="400px" maxW="400px">
        {hasRecommendedGroups ? (
          <>
            <Flex gap={2}>
              <Tooltip label="Recommended for this article">
                <Icons.ThumbUp
                  animation={moveDownRotateAnimation()}
                  boxSize={defaultIconSize}
                  color="texas.sand.50"
                />
              </Tooltip>
              <Heading fontSize="md">{t('sizes.recommended')}</Heading>
            </Flex>

            {groups
              ?.filter((x) =>
                x.linkedProductGroupIds.some(
                  (l) => composition.articleProductGroupId === l,
                ),
              )
              .map((x) => {
                return (
                  <DimensionGroupButton
                    key={x.id}
                    onClick={() => {
                      setSelectedGroupId(x.id);
                      onChange(x.dimensions.map((d) => d.id));
                    }}
                    group={x}
                    selectedGroupId={selectedGroupId}
                  />
                );
              })}
            <Button
              mt={6}
              justifyContent="space-between"
              onClick={onToggle}
              rightIcon={
                isOpen ? (
                  <Icons.ChevronUp boxSize={defaultIconSize} />
                ) : (
                  <Icons.ChevronDown boxSize={defaultIconSize} />
                )
              }
            >
              {t('sizes.other')}
            </Button>
            <Collapse in={isOpen} animateOpacity={true}>
              <Flex flexDir="column" gap={2} padding={2}>
                {groups
                  ?.filter(
                    (x) =>
                      !x.linkedProductGroupIds.some(
                        (l) => composition.articleProductGroupId === l,
                      ),
                  )
                  .map((x) => {
                    return (
                      <DimensionGroupButton
                        key={x.id}
                        onClick={() => {
                          setSelectedGroupId(x.id);
                          onChange(x.dimensions.map((d) => d.id));
                        }}
                        group={x}
                        selectedGroupId={selectedGroupId}
                      />
                    );
                  })}
              </Flex>
            </Collapse>
          </>
        ) : (
          <>
            {groups
              ?.filter(
                (x) =>
                  !x.linkedProductGroupIds.some(
                    (l) => composition.articleProductGroupId === l,
                  ),
              )
              .map((x) => {
                return (
                  <DimensionGroupButton
                    key={x.id}
                    onClick={() => {
                      setSelectedGroupId(x.id);
                      onChange(x.dimensions.map((d) => d.id));
                    }}
                    group={x}
                    selectedGroupId={selectedGroupId}
                  />
                );
              })}
          </>
        )}
      </Flex>

      <Box pos="sticky" top={0} h="fit-content">
        {selectedDimensionGroup && (
          <Flex animation={fadeInRightAnimation()} flexDir="column" gap={4}>
            {selectedDimensionGroup.description.length > 0 && (
              <>
                <Flex align="center" gap={2}>
                  <Icons.InformationOutline boxSize={defaultIconSize} />
                  <Heading fontSize="md">{t('general.description')}</Heading>
                </Flex>
                <Text>{selectedDimensionGroup.description}</Text>
              </>
            )}

            <Flex align="center" gap={2}>
              <Icons.AxisArrow boxSize={defaultIconSize} />
              <Heading fontSize="md">{t('sizes.available')}</Heading>
            </Flex>
            <Flex flexWrap="wrap" gap={2}>
              {selectedDimensionGroup.dimensions.map((d) => (
                <Badge key={d.id}>{d.name}</Badge>
              ))}
            </Flex>
            {selectedDimensionGroup.file && (
              <Img
                maxH="40"
                objectFit="contain"
                borderRadius="md"
                w="fit-content"
                src={clientEndpoints.previewImage(
                  selectedDimensionGroup.file.identifier,
                  128,
                )}
              />
            )}
            <Box>
              <TexasFormLabel>{t('sizes.selectMetric')}</TexasFormLabel>
              <TexasSelect
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                value={{
                  label: metricFriendlyName(selectedMetric),
                  value: selectedMetric,
                }}
                onChange={(e) => {
                  onMetricChange(e?.value as Metric);
                }}
                options={getEnumNamesAndValues(Metric).map((s) => {
                  return {
                    label: metricFriendlyName(s.value as Metric),
                    value: s.value,
                  };
                })}
              />
              {selectedMetric !==
                convertToEnum(Metric, firstDimension?.metric) && (
                <SimpleNote
                  pt={2}
                  color="yellow.300"
                  _light={{ color: 'orange.500' }}
                >
                  {t('sizes.changeMetricNote')}
                </SimpleNote>
              )}
            </Box>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}

function DimensionGroupButton({
  onClick,
  selectedGroupId,
  group,
}: {
  onClick: () => void;
  group: DimensionGroup;
  selectedGroupId?: number;
}) {
  const { colorMode } = useColorMode();

  return (
    <Button
      {...navLinkOffsetStyle(group.id === selectedGroupId, 1.02, colorMode)}
      h="auto"
      py={2}
      gap={2}
      alignItems="center"
      justifyContent="space-between"
      textAlign="start"
      _light={{
        bg: 'gray.50',
      }}
      onClick={onClick}
    >
      <Flex flexDir="column" gap={2} whiteSpace="normal">
        <Heading fontSize="md" wordBreak="break-word">
          {group.name}
        </Heading>
        <Flex flexWrap="wrap" gap={2}>
          {group.dimensions.map((d) => (
            <Badge key={d.id}>{d.name}</Badge>
          ))}
        </Flex>
      </Flex>
      {group.visualFile && (
        <Img
          w="auto"
          borderRadius="md"
          src={clientEndpoints.previewImage(group.visualFile.identifier, 128)}
        />
      )}
    </Button>
  );
}
